// Lays out Bomb the Hill's course (#194 to #197; split out of
// `hill-course.ts` by #208): `CourseGen` a chunk at a time from a seed, the
// rows and their ways through, the crosswalks' people and the side streets'
// cross traffic, each checked to always leave a way through.

import {
  CHUNK,
  COURSE,
  CROSSING,
  CROSSING_STEP,
  driveTime,
  GRIND,
  HILL_VEHICLES,
  JUNCTION,
  keyX,
  loopX,
  PASS_WIDTH,
  RIDER_R,
  sideStart,
  type CourseChunk,
  type CourseTuning,
  type HillCrosser,
  type HillCrossing,
  type HillGap,
  type HillGrind,
  type HillJunction,
  type HillLight,
  type HillObstacle,
  type HillRamp,
  type HillTraffic,
  type HillVehicleKind,
  type HillWalker,
} from "./hill-course";

/** The small things' sizes. A barrier and a gravel patch are as wide as they're placed. */
const CONE = { hw: 0.2, hl: 0.2, h: 0.7 };
const BIN = { hw: 0.3, hl: 0.3, h: 1.05 };
const BARRIER = { hl: 0.3, h: 0.85 };
const RAIL = { hw: 0.06, h: 0.5 };
const POTHOLE = { hw: 0.45, hl: 0.35 };

/** Small deterministic RNG (mulberry32). */
function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** Mixes a run's seed with a chunk's index into that chunk's own seed. */
function chunkSeed(seed: number, k: number): number {
  let h = (seed ^ Math.imul(k + 1, 0x9e3779b1)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b) >>> 0;
  return (h ^ (h >>> 13)) >>> 0;
}

/**
 * The road a rail at x (#237) leaves before the next row (way through `next`):
 * enough to carve from its line, coming off its end or round it, across to
 * that way through (as far across as rows' ways through ever shift), and
 * never less than `GRIND.runout`.
 */
export function grindRunout(x: number, next: HillGap, shift: number = COURSE.shift): number {
  const to = Math.min(Math.max(x, next.x0 + RIDER_R), next.x1 - RIDER_R);
  return Math.max(GRIND.runout, (Math.abs(x - to) + GRIND.clear) / shift + 2);
}

/** Mixed into a run's seed for the grind rails' own random stream (#237), so laying them out leaves everything else where it was. */
const GRIND_SALT = 0x6a09e667;

/** The next seed after `seed` in a run of seeds (each run's own, #195, when nothing else picks one). */
export function nextSeed(seed: number): number {
  return chunkSeed(seed, -2);
}

/**
 * The open stretches (in the rider's centre, `RIDER_R` either side of each
 * walker) across the road `halfWidth` wide, between walkers standing at
 * `xs` (any order).
 */
export function openBetween(xs: readonly number[], halfWidth: number, hw: number = CROSSING.walker.hw): [number, number][] {
  const sorted = [...xs].sort((p, q) => p - q);
  const open: [number, number][] = [];
  let x = -halfWidth + RIDER_R;
  for (const w of sorted) {
    const lo = w - hw - RIDER_R;
    if (lo > x) open.push([x, lo]);
    x = Math.max(x, w + hw + RIDER_R);
  }
  if (halfWidth - RIDER_R > x) open.push([x, halfWidth - RIDER_R]);
  return open;
}

/** Whether walkers at `xs` leave a way through at least `CROSSING.open` wide that reaches into x0 .. x1. */
export function leavesWay(xs: readonly number[], halfWidth: number, x0: number, x1: number, open: number = CROSSING.open): boolean {
  return openBetween(xs, halfWidth).some(([lo, hi]) => hi - lo >= open && hi - open / 2 >= x0 && lo + open / 2 <= x1);
}

/**
 * The people on a crosswalk at z (#196), `tightness` into the ranges (how hard the road is there, #206): one to
 * three groups of one to three, more the harder it is. Each group comes up
 * the footpath from the alley on its side (#202), waits on the pavement at
 * the dropped kerb, walks across at its own pace, maybe stopping halfway or
 * turning back on the way, and carries on down the far footpath; then, a
 * while later, back the other way. The people in a group walk side by side
 * (along the road), a step behind each other. Every group's cycle is padded
 * at the end of its footpath to one shared length, and a group is left out
 * if, at any moment through that cycle, the crossing wouldn't leave a way
 * through that reaches into `way` (x0 .. x1).
 */
function crowd(rand: () => number, W: number, z: number, tightness: number, way: { x0: number; x1: number }): HillWalker[] {
  const { hw, h } = CROSSING.walker;
  const kerb = W + CROSSING.kerb;
  const out = W + CROSSING.path;
  interface Group {
    legs: { t: number; x: number }[];
    size: number;
    phase: number;
    tint: number;
  }
  const groups: Group[] = [];
  const n = Math.min(3, 1 + Math.floor(rand() * (1.6 + 2 * tightness)));
  for (let g = 0; g < n; g++) {
    const pace = lerp(CROSSING.pace.min, CROSSING.pace.max, rand());
    const from = rand() < 0.5 ? -1 : 1;
    const keys: { t: number; x: number }[] = [{ t: 0, x: from * out }];
    const at = () => keys[keys.length - 1]!;
    const walk = (x: number) => keys.push({ t: at().t + Math.abs(x - at().x) / pace, x });
    const wait = (s: number) => keys.push({ t: at().t + s, x: at().x });
    /** One way across, kerb to kerb: straight over, a stop partway, or a turn back partway and then over. */
    const cross = (to: number) => {
      const roll = rand();
      const part = at().x + (to - at().x) * lerp(0.3, 0.65, rand());
      if (roll < 0.25) {
        walk(part);
        wait(lerp(1, 2.5, rand()));
      } else if (roll < 0.4) {
        const start = at().x;
        walk(part);
        wait(0.4);
        walk(start);
        wait(lerp(0.6, 1.5, rand()));
      }
      walk(to);
    };
    /** Up the footpath to the kerb, a look for a gap, over, and on down the far footpath. */
    // A look both ways at the kerb is no draw of its own, so the rest of the chunk lays out as it did before #202.
    const over = (side: number) => {
      walk(side * kerb);
      wait(CROSSING.look);
      cross(-side * kerb);
      walk(-side * out);
      wait(lerp(1, 3.5, rand()));
    };
    over(from);
    over(-from);
    const size = rand() < 0.55 ? 1 : rand() < 0.75 ? 2 : 3;
    groups.push({ legs: keys, size, phase: 0, tint: rand() });
  }
  for (const g of groups) g.phase = rand();
  // Leave out the last group until the rest always leave a way through.
  while (groups.length) {
    const period = Math.max(...groups.map((g) => g.legs.at(-1)!.t));
    const walkers = groups.flatMap((g) => people(g, period, z, hw, h));
    if (alwaysOpen(walkers, W, way)) return walkers;
    groups.pop();
  }
  return [];
}

/** A group's people (#196): its cycle padded to `period` at the curb, side by side along the road, each a step behind the last. */
function people(g: { legs: { t: number; x: number }[]; size: number; phase: number; tint: number }, period: number, z: number, hw: number, h: number): HillWalker[] {
  const keys = [...g.legs];
  if (keys.at(-1)!.t < period) keys.push({ t: period, x: keys.at(-1)!.x });
  return Array.from({ length: g.size }, (_, i) => ({
    z: z + (i - (g.size - 1) / 2) * 0.7,
    keys,
    period,
    phase: g.phase * period - i * 0.25,
    hw,
    h,
    tint: (g.tint + i * 0.37) % 1,
  }));
}

/**
 * Whether `walkers` (all on one crossing, one cycle) always leave a way
 * through reaching into `way`. It's checked every `CROSSING_STEP`, each
 * time a little wider than it must be, by as far as two people closing in
 * could walk in a step, so it's open in between too.
 */
function alwaysOpen(walkers: readonly HillWalker[], W: number, way: { x0: number; x1: number }): boolean {
  const period = walkers[0]?.period ?? 0;
  const open = CROSSING.open + 2 * CROSSING.pace.max * CROSSING_STEP;
  for (let t = 0; t <= period; t += CROSSING_STEP) {
    if (!leavesWay(walkers.map((w) => keyX(w, t + w.phase)), W, way.x0, way.x1, open)) return false;
  }
  return true;
}

/**
 * The stretches of road a lane's vehicles (#197) block for a rider (`RIDER_R`
 * either side) over `t` .. `t + dt`, as they drive across it.
 */
function laneBlocks(cars: readonly HillCrosser[], t: number, dt: number): [number, number][] {
  return cars.map((c) => {
    const a = loopX(c, t);
    const b = loopX(c, t + dt);
    // Looping round in that time: it's off the road at the ends of its loop anyway, so only what's on the road counts.
    const [lo, hi] = Math.abs(b - a) > c.speed * dt + 1e-6 ? [a, a + c.dir * c.speed * dt] : [a, b];
    return [Math.min(lo, hi) - c.hl - RIDER_R, Math.max(lo, hi) + c.hl + RIDER_R];
  });
}

/** The open stretches across the road `halfWidth` wide (in the rider's centre) between `blocks`. */
function openOf(blocks: readonly [number, number][], halfWidth: number): [number, number][] {
  const sorted = [...blocks].sort((p, q) => p[0] - q[0]);
  const open: [number, number][] = [];
  let x = -halfWidth + RIDER_R;
  for (const [lo, hi] of sorted) {
    if (lo > x) open.push([x, Math.min(lo, halfWidth - RIDER_R)]);
    x = Math.max(x, hi);
  }
  if (halfWidth - RIDER_R > x) open.push([x, halfWidth - RIDER_R]);
  return open.filter(([lo, hi]) => hi > lo);
}

/** How long after reaching a side street's first lane a rider reaches its second (s): from the fastest bombing to `JUNCTION.rider`. */
const LANE_GAPS = [0.14, 0.18, 0.22, 0.26, 0.3, 0.34];

/**
 * Whether a side street's traffic (#197) always leaves a way through
 * reaching into `way`: at every `CROSSING_STEP` of its cycle, for a rider
 * holding a line across both lanes (reaching the second a moment after the
 * first), each lane swept over the time they take crossing it, a little
 * wider than it must be, by as far as two vehicles closing in drive in a
 * step, so it's open in between too.
 */
export function junctionOpen(cars: readonly HillCrosser[], z: number, halfWidth: number, way: { x0: number; x1: number }): boolean {
  if (!cars.length) return true;
  const upper = cars.filter((c) => c.z < z);
  const lower = cars.filter((c) => c.z >= z);
  const period = cars[0]!.period;
  const open = CROSSING.open + 2 * JUNCTION.pace.max * CROSSING_STEP;
  const sweep = (list: readonly HillCrosser[]) => (2 * Math.max(0, ...list.map((c) => c.hw)) + 2 * RIDER_R) / JUNCTION.rider;
  const su = sweep(upper);
  const sl = sweep(lower);
  for (let t = 0; t <= period; t += CROSSING_STEP) {
    const a = openOf(laneBlocks(upper, t, su), halfWidth);
    for (const d of LANE_GAPS) {
      const b = openOf(laneBlocks(lower, t + d, sl), halfWidth);
      const ok = a.some(([al, ah]) =>
        b.some(([bl, bh]) => {
          const lo = Math.max(al, bl);
          const hi = Math.min(ah, bh);
          return hi - lo >= open && hi - open / 2 >= way.x0 && lo + open / 2 <= way.x1;
        }),
      );
      if (!ok) return false;
    }
  }
  return true;
}

/**
 * A side street's lights (#214) `tightness` into the ranges (how hard the road is there, #206): the road's green
 * shorter and the side street's longer the harder it is, the cycle starting
 * anywhere when the run's traffic sets off.
 */
function lights(rand: () => number, tightness: number): HillLight {
  const L = JUNCTION.light;
  const roadGreen = lerp(L.roadTop, L.roadBottom, tightness) * (0.9 + 0.2 * rand());
  const sideGreen = lerp(L.sideTop, L.sideBottom, tightness);
  const period = roadGreen + sideGreen + 2 * (L.amber + L.allRed);
  return { roadGreen, sideGreen, amber: L.amber, allRed: L.allRed, period, phase: rand() * period };
}

/** How long (s) something pulling away from standing at `JUNCTION.accel` up to `v` takes to go `d` metres. */
function pullAway(v: number, d: number): number {
  const a = JUNCTION.accel;
  const da = (v * v) / (2 * a);
  return d <= da ? Math.sqrt((2 * d) / a) : v / a + (d - da) / v;
}

/**
 * A side street's traffic (#197) at z, `tightness` into the ranges (how hard the road is there, #206), to its
 * lights (#214): a lane each way, the upper one (up the hill) driving toward
 * -x, the lower toward +x, each at its own pace, looping out of sight past
 * the street's far ends to one cycle. Each lane's vehicles queue at its stop
 * line on red and pull away one after another on green. Two to five vehicles
 * are tried (more the harder it is): one goes in at the back of its lane's queue
 * if it's across the road before the side street's amber runs out and the
 * traffic still leaves a way through (`junctionOpen`), or not at all.
 */
function crossTraffic(rand: () => number, W: number, z: number, tightness: number, way: { x0: number; x1: number }): { light: HillLight; cars: HillCrosser[] } {
  const light = lights(rand, tightness);
  const go = sideStart(light);
  const span = 2 * (W + JUNCTION.street);
  // Every lap fits in a cycle with time to wait at the stop: as slow as its pace allows.
  const fastest = JUNCTION.pace.max;
  const lanes = ([-1, 1] as const).map((dir) => {
    let speed = lerp(JUNCTION.pace.min, fastest, rand());
    while (speed < fastest && driveTime(speed, span) > light.period - 1) speed = Math.min(fastest, speed + 0.25);
    return { dir, z: z + dir * JUNCTION.lane, speed, queue: [] as HillCrosser[] };
  });
  const cars: HillCrosser[] = [];
  const n = 2 + Math.floor(rand() * (1.5 + 2 * tightness));
  for (let i = 0; i < n; i++) {
    const lane = lanes[i % 2 === 0 ? (rand() < 0.5 ? 0 : 1) : cars.length && cars.at(-1)!.dir < 0 ? 1 : 0]!;
    // No buses: one's longer than the road is wide, so crossing it would shut it.
    const kind = rand() < 0.7 ? "car" : "van";
    const v = HILL_VEHICLES[kind];
    const tint = rand();
    const ahead = lane.queue.at(-1);
    const rest = ahead ? ahead.rest - ahead.hl - JUNCTION.queue - v.hl : -(W + JUNCTION.stop) - v.hl;
    const q = lane.queue.length;
    // Across the road (its back past the far curb) before the side street's amber runs out, and queued short of where it loops round.
    const across = q * JUNCTION.react + pullAway(lane.speed, W + v.hl - rest);
    if (across > light.sideGreen + light.amber || rest - v.hl < -span / 2 + 2) continue;
    const car: HillCrosser = { kind, z: lane.z, hl: v.hl, hw: v.hw, h: v.h, dir: lane.dir, speed: lane.speed, span, period: light.period, phase: light.phase, rest, go: go + q * JUNCTION.react, tint };
    if (!junctionOpen([...cars, car], z, W, way)) continue;
    cars.push(car);
    lane.queue.push(car);
  }
  return { light, cars };
}

/**
 * Lays out a course chunk by chunk (#195), on the road `halfWidth` either
 * side of x = 0, from `z0` (where you start) on down forever. Each chunk
 * draws from its own random stream (the run's seed and its index), and a
 * row's way through carries on from the last chunk's, so chunks are laid
 * out in order and the same seed always lays out the same road.
 */
export class CourseGen {
  readonly lane: { x0: number; x1: number };
  /** The street: the road outside the traffic lane. */
  private a: number;
  private b: number;
  /** The next row's z, the last row's z and where its way through was. */
  private z: number;
  private last: number;
  private centre = 0;
  /** Whether a crosswalk (#196) went in after the last row, so the next row gets no ramp. */
  private crossed = false;
  /** The longest (half-length) the next row's parked vehicles may be: short of a crosswalk before it. */
  private longest = Infinity;
  /** The next chunk to lay out. */
  private k = 0;
  /** The next row's number, from 0 (for the tutorial's road, #201). */
  private row = 0;
  /** The chunk being laid out's difficulty past the warm-up (#206): `tuning.level`, give or take its swing. */
  private level = 0;

  constructor(
    readonly seed: number,
    readonly halfWidth: number,
    readonly z0: number,
    /** How it's laid out (#208): spacing, ways through, how often a crosswalk or side street, the traffic. */
    readonly tuning: CourseTuning = COURSE,
  ) {
    const W = halfWidth;
    const side = seeded(seed)() < 0.5 ? -1 : 1;
    this.lane = side > 0 ? { x0: W - this.tuning.laneWidth, x1: W } : { x0: -W, x1: -W + this.tuning.laneWidth };
    this.a = side > 0 ? -W : this.lane.x1;
    this.b = side > 0 ? this.lane.x0 : W;
    this.z = z0 + this.tuning.firstRow;
    this.last = z0;
  }

  /**
   * How hard the road is at `z` (#206): `warm`, how much of the warm-up is
   * left (1 through it, easing to 0 over `tuning.easeIn` after it), and `t`,
   * where in the ranges the rows sit (0 the easiest, 1 the hardest), easing
   * up to the chunk's level as the warm-up eases off and holding it there.
   */
  difficulty(z: number): { warm: number; t: number } {
    const { warmup, easeIn } = this.tuning;
    const ease = Math.min(1, Math.max(0, (z - this.z0 - warmup.length) / easeIn));
    return { warm: 1 - ease, t: ease * this.level };
  }

  /** Lays out the next chunk. */
  next(): CourseChunk {
    const k = this.k++;
    const rand = seeded(chunkSeed(this.seed, k));
    const W = this.halfWidth;
    const { a, b } = this;
    const end = this.z0 + (k + 1) * CHUNK;
    const obstacles: HillObstacle[] = [];
    const ramps: HillRamp[] = [];
    const gaps: HillGap[] = [];
    const crossings: HillCrossing[] = [];
    const walkers: HillWalker[] = [];
    const junctions: HillJunction[] = [];
    const crossers: HillCrosser[] = [];
    const pick = <T>(list: readonly T[]): T => list[Math.floor(rand() * list.length)]!;
    const { tuning } = this;
    this.level = Math.min(1, Math.max(0, tuning.level + tuning.swing * (rand() * 2 - 1)));

    /** Blocks x lo .. hi across the row at z: nothing a skater fits through is left open. */
    const fill = (lo: number, hi: number, z: number): void => {
      const len = hi - lo;
      if (len < PASS_WIDTH) return;
      const roll = rand();
      const kind = pick<HillVehicleKind>(["car", "car", "van", "bus"]);
      const v = HILL_VEHICLES[kind];
      if (roll < 0.6 && len >= v.hw * 2 && v.hl <= this.longest) {
        // A parked vehicle against one end; whatever's left is filled too.
        const atLo = rand() < 0.5;
        const x = atLo ? lo + v.hw : hi - v.hw;
        obstacles.push({ kind, x, z, ...v, tint: rand() });
        if (atLo) fill(lo + v.hw * 2, hi, z);
        else fill(lo, hi - v.hw * 2, z);
      } else if (roll < 0.75 && len <= 4.5) {
        obstacles.push({ kind: "barrier", x: (lo + hi) / 2, z, hw: len / 2, ...BARRIER, tint: rand() });
      } else {
        // A line of cones or bins, spaced so nobody fits between them.
        const small = rand() < 0.6 ? { kind: "cone" as const, ...CONE, step: 0.85 } : { kind: "bin" as const, ...BIN, step: 1.1 };
        const n = Math.max(1, Math.ceil((len - small.hw * 2) / small.step) + 1);
        const span = len - small.hw * 2;
        for (let i = 0; i < n; i++) {
          const x = n === 1 ? (lo + hi) / 2 : lo + small.hw + (span * i) / (n - 1);
          obstacles.push({ kind: small.kind, x, z, hw: small.hw, hl: small.hl, h: small.h, tint: rand() });
        }
      }
    };

    while (this.z < end) {
      const z = this.z;
      const row = this.row++;
      const lesson = tuning.lessons;
      // Through the warm-up (#206) the rows are further apart and wider open, and nothing crosses the road.
      const { warm, t } = this.difficulty(z);
      const gap = lerp(lerp(tuning.gapTop, tuning.gapBottom, t), tuning.warmup.gap, warm);
      const reach = (z - this.last) * tuning.shift;
      const lo = a + gap / 2;
      const hi = b - gap / 2;
      // A side street (#197) after this row: its traffic sweeps the whole road, so this row's way through is as near the middle as it can be.
      const junction = row === lesson?.junction || rand() < (tuning.junctionTop + tuning.junctionMore * t) * (1 - warm);
      const aim = junction ? Math.min(this.centre + reach, Math.max(this.centre - reach, 0)) : this.centre + (rand() * 2 - 1) * reach;
      this.centre = Math.min(hi, Math.max(lo, aim));
      const x0 = this.centre - gap / 2;
      const x1 = this.centre + gap / 2;
      const from = obstacles.length;
      fill(a, x0, z);
      fill(x1, b, z);
      this.longest = Infinity;
      const reachBack = Math.max(0, ...obstacles.slice(from).map((o) => o.hl));
      gaps.push({ z, x0, x1 });
      // A ramp in the way through, to pop off and fly the row (not right behind a crosswalk).
      if (gap >= 2.3 && rand() < 0.3 && !this.crossed) ramps.push({ x: this.centre, z: z - 5, hw: gap / 2 - 0.35, len: 2.2, h: 0.55 });

      let spacing = lerp(lerp(tuning.spacingTop, tuning.spacingBottom, t), tuning.warmup.spacing, warm) * (0.85 + 0.3 * rand());
      // Between this row and the next: a side street (#197), a crosswalk (#196), or a pothole, a gravel patch or a low rail, more of them the harder it is.
      // A side street needs the road clear either side of it, so the rows make room for it: room to carve to a gap in its traffic too.
      if (junction) spacing = Math.max(spacing, 2 * (JUNCTION.hl + Math.max(reachBack, JUNCTION.approach)));
      const mid = z + spacing / 2;
      this.crossed = false;
      // A crosswalk needs the road clear either side of it: past this row's longest vehicle, and the next row's is kept short enough.
      const room = spacing / 2 - CROSSING.hl;
      if (junction) {
        // Cross traffic sweeps the whole road, so its way through can be as far from the row's as you carve getting to its first lane.
        const reach = Math.max(CROSSING.reach, (spacing / 2 - JUNCTION.lane - HILL_VEHICLES.bus.hw) * tuning.shift);
        const way = { x0: x0 - reach, x1: x1 + reach };
        const { light, cars } = crossTraffic(rand, W, mid, t, way);
        junctions.push({ light, z: mid, hl: JUNCTION.hl, street: JUNCTION.street, ...way });
        crossers.push(...cars);
        // No ramp in front of the next row, nor anything parked reaching into the side street.
        this.crossed = true;
        this.longest = spacing / 2 - JUNCTION.hl - 0.05;
      } else if ((row === lesson?.crossing || rand() < (tuning.crossingTop + tuning.crossingMore * t) * (1 - warm)) && room > reachBack + 0.05) {
        const way = { x0: x0 - CROSSING.reach, x1: x1 + CROSSING.reach };
        crossings.push({ z: mid, hl: CROSSING.hl, ...way });
        walkers.push(...crowd(rand, W, mid, t, way));
        // Its people's z reaches toward the next row: no ramp in front of that one, nor anything parked that long.
        this.crossed = true;
        this.longest = room - 0.05;
      } else if (rand() < lerp(0.35 + 0.4 * t, tuning.warmup.extras, warm)) {
        const what = rand();
        if (what < 0.45) obstacles.push({ kind: "pothole", x: lerp(-W + 0.6, W - 0.6, rand()), z: mid, ...POTHOLE, h: 0, tint: rand() });
        else if (what < 0.75) {
          const hw = lerp(1, 1.8, rand());
          obstacles.push({ kind: "gravel", x: lerp(-W + hw, W - hw, rand()), z: mid, hw, hl: lerp(2, 3.5, rand()), h: 0, tint: rand() });
        } else obstacles.push({ kind: "rail", x: lerp(a + 0.8, b - 0.8, rand()), z: mid, hw: RAIL.hw, hl: lerp(2, 3.5, rand()), h: RAIL.h, tint: rand() });
      }
      this.last = z;
      this.z = z + spacing;
    }

    // Traffic: a vehicle per `trafficEvery` metres on average, starting somewhere in this chunk.
    const traffic: HillTraffic[] = [];
    if (rand() < CHUNK / tuning.trafficEvery) {
      const kind = pick<HillVehicleKind>(["car", "car", "van", "bus"]);
      const v = HILL_VEHICLES[kind];
      const x = (this.lane.x0 + this.lane.x1) / 2;
      const speed = lerp(tuning.trafficSpeed.min, tuning.trafficSpeed.max, rand());
      const start = end - CHUNK + CHUNK * rand();
      const tint = rand();
      // None sets off in the warm-up (#206): it only drives on down, so the warm-up stays clear of it.
      if (start >= this.z0 + tuning.warmup.length) traffic.push({ kind, x, ...v, speed, start, tint });
    }
    obstacles.sort((p, q) => p.z - q.z);
    walkers.sort((p, q) => p.z - q.z);
    crossers.sort((p, q) => p.z - q.z);
    const grinds = this.grinds(k, obstacles, ramps, gaps, [...crossings, ...junctions]);
    return { k, obstacles, traffic, ramps, grinds, gaps, crossings, walkers, junctions, crossers };
  }

  /**
   * The rails to grind (#237) in chunk `k`, on their own random stream so the
   * rest of the chunk lays out as it did before them. Between two of its
   * rows with nothing crossing the road between them, sometimes (more the
   * harder the road, none in the warm-up), a rail along the road just
   * beside the first row's way through, out of the traffic lane: from clear
   * of that row on down to where coming off it at speed lands on open road
   * with room to carve to the next row's way through. Never near anything
   * solid, so a grind (which nothing in the road can hit, `courseHit`)
   * never carries you through anything.
   */
  private grinds(k: number, obstacles: readonly HillObstacle[], ramps: readonly HillRamp[], gaps: readonly HillGap[], across: readonly { z: number; hl: number }[]): HillGrind[] {
    const rand = seeded(chunkSeed(this.seed ^ GRIND_SALT, k));
    const W = this.halfWidth;
    const { lane } = this;
    const out: HillGrind[] = [];
    for (let i = 0; i + 1 < gaps.length; i++) {
      const g = gaps[i]!;
      const next = gaps[i + 1]!;
      const { warm, t } = this.difficulty(g.z);
      if (rand() >= (GRIND.top + GRIND.more * t) * (1 - warm)) continue;
      if (across.some((c) => c.z + c.hl > g.z && c.z - c.hl < next.z)) continue;
      const first = rand() < 0.5 ? 1 : -1;
      const h = lerp(GRIND.h.min, GRIND.h.max, rand());
      for (const side of [first, -first]) {
        const x = side > 0 ? g.x1 + GRIND.beside : g.x0 - GRIND.beside;
        const r = this.grindAt(x, g, next, obstacles, ramps, W, lane);
        if (r) {
          out.push({ x, ...r, h });
          break;
        }
      }
    }
    return out;
  }

  /** Where a rail at x between rows `g` and `next` runs (#237): its top end and length, or null if it doesn't fit there. */
  private grindAt(x: number, g: HillGap, next: HillGap, obstacles: readonly HillObstacle[], ramps: readonly HillRamp[], W: number, lane: { x0: number; x1: number }): { z: number; len: number } | null {
    const c = GRIND.clear;
    if (Math.abs(x) > W - c || (x > lane.x0 - c && x < lane.x1 + c)) return null;
    const near = (o: { x: number; hw: number }) => Math.abs(o.x - x) <= o.hw + c;
    // From clear of the row's own things on this side...
    let z0 = g.z + 1;
    for (const o of obstacles) if (o.z === g.z && near(o)) z0 = Math.max(z0, o.z + o.hl + c + 0.4);
    // ...down to where you can still carve round its end (or off it) to the next row's way through, and land clear of anything in the way.
    let z1 = next.z - grindRunout(x, next, this.tuning.shift);
    for (const o of obstacles) {
      if (o.kind === "gravel" || o.z <= g.z || !near(o)) continue;
      if (o.z - o.hl - c < z0) return null;
      z1 = Math.min(z1, o.z - o.hl - c - GRIND.land);
    }
    for (const r of ramps) {
      if (r.z + r.len <= g.z || r.z >= next.z + 0.01 || Math.abs(r.x - x) > r.hw + c) continue;
      if (r.z - c < z0) return null;
      z1 = Math.min(z1, r.z - c - GRIND.land);
    }
    const len = Math.min(GRIND.len.max, z1 - z0);
    return len >= GRIND.len.min ? { z: z0, len } : null;
  }
}
